// Meal Browser — signed-in customer-facing meal selection page.
//
// Data source: public.meal_planner_view (queried via canonical
// Supabase client through lib/mealCatalog.js).  No hardcoded meal
// nutrition anywhere in this file.  All displayed nutrition values
// come from the Supabase catalog row for that product.
//
// UX contract:
//   * Anonymous visitors are redirected to /sign-in.
//   * Loading state: standard planner spinner text.
//   * Error state: single customer-safe sentence; no DB internals.
//   * Products render grouped by exact database category label, in
//     display_order ASC within each category.
//   * A quantity control lets the customer pick 0..10 servings.
//   * Live totals (calories, protein, carbs, fat) are computed from
//     the DB nutrition values × selected quantities via
//     lib/nutritionTotals.js.
//   * A small "Catalog" summary at the top shows total products,
//     unique ETM count, first/last product by display_order, and
//     per-category counts.  This is intentional and small — it
//     doubles as the phase's catalog-verification surface.

import React, { useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { AppShell } from '../../components/AppShell';
import { BrandButton } from '../../components/BrandButton';
import { useSession } from '../../lib/useSession';
import { useMealCatalog } from '../../lib/mealCatalog';
import { computeSelectionTotals } from '../../lib/nutritionTotals';

const MAX_QTY_PER_MEAL = 10;

function fmtInt(n) {
  return Number.isFinite(n) ? Math.round(n).toString() : '—';
}
function fmtDec1(n) {
  return Number.isFinite(n) ? (Math.round(n * 10) / 10).toString() : '—';
}

function MealCard({ meal, quantity, onQty }) {
  return (
    <div
      className="p-4"
      style={{
        border: '1px solid var(--line)',
        borderRadius: 'var(--radius)',
        background: 'var(--paper)',
      }}
      data-testid={`meal-card-${meal.etm_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div
            className="eyebrow"
            style={{ color: 'var(--text-dim)' }}
            data-testid={`meal-etm-${meal.etm_id}`}
          >
            #{meal.display_order} · {meal.etm_id}
          </div>
          <div
            className="font-display text-lg leading-tight mt-1"
            style={{ color: 'var(--ink)' }}
            data-testid={`meal-name-${meal.etm_id}`}
          >
            {meal.name}
          </div>
          {meal.serving_size && (
            <div
              className="text-xs mt-1"
              style={{ color: 'var(--text-dim)' }}
              data-testid={`meal-serving-${meal.etm_id}`}
            >
              Serving: {meal.serving_size}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            aria-label={`Decrease quantity of ${meal.name}`}
            onClick={() => onQty(Math.max(0, quantity - 1))}
            disabled={quantity <= 0}
            className="btn btn-outline btn-sm"
            data-testid={`meal-qty-dec-${meal.etm_id}`}
          >
            −
          </button>
          <span
            className="w-8 text-center font-display"
            data-testid={`meal-qty-${meal.etm_id}`}
          >
            {quantity}
          </span>
          <button
            type="button"
            aria-label={`Increase quantity of ${meal.name}`}
            onClick={() => onQty(Math.min(MAX_QTY_PER_MEAL, quantity + 1))}
            disabled={quantity >= MAX_QTY_PER_MEAL}
            className="btn btn-outline btn-sm"
            data-testid={`meal-qty-inc-${meal.etm_id}`}
          >
            +
          </button>
        </div>
      </div>

      <div
        className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-2 text-sm"
        style={{ color: 'var(--ink-2)' }}
      >
        <div>
          <div
            className="eyebrow whitespace-nowrap"
            style={{ color: 'var(--text-dim)', fontSize: '10px', letterSpacing: '0.14em' }}
          >
            Calories
          </div>
          <div data-testid={`meal-cal-${meal.etm_id}`}>{fmtInt(meal.calories)}</div>
        </div>
        <div>
          <div
            className="eyebrow whitespace-nowrap"
            style={{ color: 'var(--text-dim)', fontSize: '10px', letterSpacing: '0.14em' }}
          >
            Protein
          </div>
          <div data-testid={`meal-protein-${meal.etm_id}`}>{fmtDec1(meal.protein_g)} g</div>
        </div>
        <div>
          <div
            className="eyebrow whitespace-nowrap"
            style={{ color: 'var(--text-dim)', fontSize: '10px', letterSpacing: '0.14em' }}
          >
            Carbs
          </div>
          <div data-testid={`meal-carbs-${meal.etm_id}`}>{fmtDec1(meal.carbs_g)} g</div>
        </div>
        <div>
          <div
            className="eyebrow whitespace-nowrap"
            style={{ color: 'var(--text-dim)', fontSize: '10px', letterSpacing: '0.14em' }}
          >
            Fat
          </div>
          <div data-testid={`meal-fat-${meal.etm_id}`}>{fmtDec1(meal.fat_g)} g</div>
        </div>
      </div>

      {(meal.fiber_g !== null ||
        meal.sodium_mg !== null ||
        meal.sugars_g !== null ||
        (Array.isArray(meal.allergens) && meal.allergens.length > 0)) && (
        <div
          className="mt-3 pt-3 text-xs"
          style={{
            color: 'var(--text-dim)',
            borderTop: '1px dashed var(--line)',
          }}
        >
          {meal.fiber_g !== null && (
            <span data-testid={`meal-fiber-${meal.etm_id}`}>
              Fiber {fmtDec1(meal.fiber_g)} g&nbsp;·&nbsp;
            </span>
          )}
          {meal.saturated_fat_g !== null && (
            <span data-testid={`meal-satfat-${meal.etm_id}`}>
              Sat fat {fmtDec1(meal.saturated_fat_g)} g&nbsp;·&nbsp;
            </span>
          )}
          {meal.sodium_mg !== null && (
            <span data-testid={`meal-sodium-${meal.etm_id}`}>
              Sodium {fmtInt(meal.sodium_mg)} mg&nbsp;·&nbsp;
            </span>
          )}
          {meal.sugars_g !== null && (
            <span data-testid={`meal-sugars-${meal.etm_id}`}>
              Sugars {fmtDec1(meal.sugars_g)} g&nbsp;·&nbsp;
            </span>
          )}
          {meal.added_sugars_g !== null && (
            <span data-testid={`meal-added-sugars-${meal.etm_id}`}>
              Added sugars {fmtDec1(meal.added_sugars_g)} g&nbsp;·&nbsp;
            </span>
          )}
          {meal.cholesterol_mg !== null && (
            <span data-testid={`meal-cholesterol-${meal.etm_id}`}>
              Cholesterol {fmtInt(meal.cholesterol_mg)} mg
            </span>
          )}
          {Array.isArray(meal.allergens) && meal.allergens.length > 0 && (
            <div className="mt-1" data-testid={`meal-allergens-${meal.etm_id}`}>
              Contains: {meal.allergens.join(', ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MealBrowser() {
  const { session, loading: sessionLoading, configured } = useSession();
  const { meals, byCategory, loading, error, refresh } = useMealCatalog();
  const [selection, setSelection] = useState({});
  const [searchParams] = useSearchParams();
  const devDiagnostics = searchParams.get('dev') === 'catalog';

  const totals = useMemo(
    () => computeSelectionTotals(meals, selection),
    [meals, selection]
  );

  // Preserve exact database category label ordering by first-seen
  // display_order within that category.
  const categoryOrder = useMemo(() => {
    const seen = new Map();
    for (const m of meals) {
      const cat = m.category || 'Other';
      if (!seen.has(cat)) seen.set(cat, m.display_order);
    }
    return [...seen.entries()]
      .sort((a, b) => a[1] - b[1])
      .map(([cat]) => cat);
  }, [meals]);

  const summary = useMemo(() => {
    const total = meals.length;
    const uniqueIds = new Set(meals.map((m) => m.etm_id)).size;
    const first = meals[0] || null;
    const last = meals[meals.length - 1] || null;
    const categoryCounts = {};
    for (const m of meals) {
      const c = m.category || 'Other';
      categoryCounts[c] = (categoryCounts[c] || 0) + 1;
    }
    return { total, uniqueIds, first, last, categoryCounts };
  }, [meals]);

  if (!configured) {
    return (
      <AppShell>
        <div className="wizard-card">
          <div className="eyebrow">Meals</div>
          <h1 className="h2 mt-3">Meal catalog unavailable</h1>
          <p className="mt-4" style={{ color: 'var(--text-dim)' }}>
            The meal catalog is temporarily unavailable. Please try again later.
          </p>
        </div>
      </AppShell>
    );
  }

  if (sessionLoading) {
    return (
      <AppShell>
        <div className="wizard-card">
          <p style={{ color: 'var(--text-dim)' }}>Loading…</p>
        </div>
      </AppShell>
    );
  }

  if (!session) {
    return <Navigate to="/sign-in" replace state={{ returnTo: '/plan/meals' }} />;
  }

  return (
    <AppShell>
      <div className="wizard-card" data-testid="meal-browser">
        <div className="eyebrow" data-testid="meal-browser-eyebrow">Meals</div>
        <h1 className="h2 mt-3" data-testid="meal-browser-title">
          Choose your meals
        </h1>
        <p className="mt-3" style={{ color: 'var(--text-dim)' }}>
          Pick how many servings of each meal you&rsquo;d like. Your daily
          totals update as you go.
        </p>

        {/* Live totals */}
        <div
          className="mt-6 p-4 grid grid-cols-2 md:grid-cols-4 gap-4"
          style={{
            border: '1px solid var(--line)',
            borderRadius: 'var(--radius)',
            background: 'var(--paper-2, rgba(0,0,0,0.03))',
          }}
          data-testid="daily-totals"
        >
          <div>
            <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>
              Daily calories
            </div>
            <div className="font-display text-2xl" data-testid="total-calories">
              {fmtInt(totals.calories)}
            </div>
          </div>
          <div>
            <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>
              Daily protein
            </div>
            <div className="font-display text-2xl" data-testid="total-protein">
              {fmtDec1(totals.protein_g)} g
            </div>
          </div>
          <div>
            <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>
              Daily carbs
            </div>
            <div className="font-display text-2xl" data-testid="total-carbs">
              {fmtDec1(totals.carbs_g)} g
            </div>
          </div>
          <div>
            <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>
              Daily fat
            </div>
            <div className="font-display text-2xl" data-testid="total-fat">
              {fmtDec1(totals.fat_g)} g
            </div>
          </div>
        </div>

        {/* Catalog verification block — dev-only diagnostic (?dev=catalog). */}
        {devDiagnostics && (
          <div
            className="mt-4 p-3 text-xs"
            style={{
              color: 'var(--text-dim)',
              border: '1px dashed var(--line)',
              borderRadius: 'var(--radius)',
            }}
            data-testid="catalog-verification"
          >
            <span className="eyebrow" style={{ color: 'var(--ink)' }}>
              Dev diagnostics
            </span>
            <div className="mt-1">
              <span data-testid="catalog-total-count">
                Catalog: {summary.total} meals
              </span>
              <span> · </span>
              <span data-testid="catalog-unique-etm-count">
                {summary.uniqueIds} unique ETM
              </span>
              {summary.first && (
                <>
                  <span> · </span>
                  <span data-testid="catalog-first">
                    first: #{summary.first.display_order} {summary.first.name} ({summary.first.etm_id})
                  </span>
                </>
              )}
              {summary.last && (
                <>
                  <span> · </span>
                  <span data-testid="catalog-last">
                    last: #{summary.last.display_order} {summary.last.name} ({summary.last.etm_id})
                  </span>
                </>
              )}
              <div className="mt-1" data-testid="catalog-category-counts">
                {Object.entries(summary.categoryCounts)
                  .map(([c, n]) => `${c}: ${n}`)
                  .join(' · ')}
              </div>
            </div>
          </div>
        )}

        {loading && (
          <p className="mt-6" style={{ color: 'var(--text-dim)' }} data-testid="catalog-loading">
            Loading the meal catalog…
          </p>
        )}

        {error && !loading && (
          <div
            className="mt-6 p-4"
            style={{
              background: 'var(--warning-bg)',
              border: '1px solid var(--warning)',
              borderRadius: 'var(--radius)',
            }}
            data-testid="catalog-error"
          >
            <p className="text-sm" style={{ color: 'var(--ink-2)' }}>
              {error}
            </p>
            <div className="mt-3">
              <BrandButton
                type="button"
                variant="outline"
                onClick={refresh}
                data-testid="catalog-retry-btn"
              >
                Try again
              </BrandButton>
            </div>
          </div>
        )}

        {!loading && !error && meals.length === 0 && (
          <p className="mt-6" style={{ color: 'var(--text-dim)' }} data-testid="catalog-empty">
            No meals are available right now.
          </p>
        )}

        {!loading && !error && meals.length > 0 && (
          <div className="mt-8" data-testid="catalog-content">
            {categoryOrder.map((cat) => {
              const items = byCategory.get(cat) || [];
              return (
                <section
                  key={cat}
                  className="mb-8"
                  data-testid={`category-section-${cat}`}
                >
                  <h2
                    className="h3"
                    style={{ color: 'var(--ink)' }}
                    data-testid={`category-title-${cat}`}
                  >
                    {cat} <span className="eyebrow" style={{ color: 'var(--text-dim)' }}>
                      ({items.length})
                    </span>
                  </h2>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {items.map((m) => (
                      <MealCard
                        key={m.etm_id}
                        meal={m}
                        quantity={selection[m.etm_id] || 0}
                        onQty={(q) =>
                          setSelection((prev) => {
                            const next = { ...prev };
                            if (q <= 0) delete next[m.etm_id];
                            else next[m.etm_id] = q;
                            return next;
                          })
                        }
                      />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
